import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  children?: ReactNode;
}

const palettes: Record<Variant, CSSProperties> = {
  primary: {
    backgroundColor: "var(--gt-sys-accent)",
    color: "var(--gt-sys-accent-contrast)",
    borderColor: "var(--gt-sys-accent)",
  },
  secondary: {
    backgroundColor: "transparent",
    color: "var(--gt-sys-text)",
    borderColor: "var(--gt-sys-border-strong)",
  },
  ghost: {
    backgroundColor: "transparent",
    color: "var(--gt-sys-text)",
    borderColor: "transparent",
  },
  danger: {
    backgroundColor: "var(--gt-sys-danger)",
    color: "var(--gt-sys-danger-contrast)",
    borderColor: "var(--gt-sys-danger)",
  },
};

/**
 * Size table. GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01
 * (CTO Correction Pass 01) replaced the raw `minHeight: 32 | 40 | 48`
 * with component tokens and switched to the logical `minBlockSize` /
 * `minInlineSize` pair.
 *
 * The desktop values are unchanged, so every existing caller looks the
 * same on a mouse pointer. Under `@media (pointer: coarse)` the tokens
 * themselves resolve to `--gt-sys-touch-target-min` (44px), so a
 * mobile-web operator gets a WCAG 2.5.5-compliant target without any
 * caller opting in and without a second "mobile" variant. The
 * `minInlineSize` floor is what makes a very short label ("OK") or an
 * icon-only button tappable; text buttons exceed it naturally.
 */
const sizes: Record<Size, CSSProperties> = {
  sm: {
    paddingBlock: "var(--gt-sys-space-1)",
    paddingInline: "var(--gt-sys-space-3)",
    fontSize: "var(--gt-sys-font-size-sm)",
    minBlockSize: "var(--gt-cmp-button-min-block-size-sm)",
    minInlineSize: "var(--gt-cmp-button-min-inline-size-sm)",
  },
  md: {
    paddingBlock: "var(--gt-sys-space-2)",
    paddingInline: "var(--gt-sys-space-3)",
    fontSize: "var(--gt-sys-font-size-md)",
    minBlockSize: "var(--gt-cmp-button-min-block-size-md)",
    minInlineSize: "var(--gt-cmp-button-min-inline-size-md)",
  },
  lg: {
    paddingBlock: "var(--gt-sys-space-3)",
    paddingInline: "var(--gt-sys-space-4)",
    fontSize: "var(--gt-sys-font-size-lg)",
    minBlockSize: "var(--gt-cmp-button-min-block-size-lg)",
    minInlineSize: "var(--gt-cmp-button-min-inline-size-lg)",
  },
};

/**
 * The button's visual contract as a plain style object.
 *
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 exported
 * this so a control that is semantically a **link** (react-router
 * `<Link>` → `<a href>`) can carry the identical framed appearance
 * without either (a) duplicating the palette/size tables or (b) the
 * forbidden `<Link><Button/></Link>` nesting, which produces an
 * interactive element inside an interactive element.
 *
 * One styling implementation, two semantic shells: `<button>` for
 * actions, `<a>` for navigation.
 */
export function buttonStyle(options: {
  variant?: Variant;
  size?: Size;
  disabled?: boolean;
}): CSSProperties {
  const { variant = "primary", size = "md", disabled = false } = options;
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--gt-sys-space-2)",
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: "var(--gt-sys-radius-md)",
    fontWeight: "var(--gt-sys-font-weight-semibold)",
    letterSpacing: 0.1,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transitionProperty: "background-color, border-color, color, box-shadow",
    /* System, not reference: tokens.css states that referencing
       --gt-ref-* from a component is a review failure. */
    transitionDuration: "var(--gt-sys-motion-duration-fast)",
    transitionTimingFunction: "var(--gt-sys-motion-ease-out)",
    ...sizes[size],
    ...palettes[variant],
  };
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  iconStart,
  iconEnd,
  disabled,
  children,
  style,
  ...rest
}: ButtonProps) {
  const baseStyle: CSSProperties = {
    ...buttonStyle({ variant, size, disabled: Boolean(disabled) || loading }),
    ...style,
  };

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      style={baseStyle}
    >
      {iconStart ? <span aria-hidden="true">{iconStart}</span> : null}
      <span>{children}</span>
      {iconEnd ? <span aria-hidden="true">{iconEnd}</span> : null}
    </button>
  );
}
